// /app/frontend/src/data/modulesConduite.js
// Modules interactifs "Cours en voiture" — supports pédagogiques riches à animer par l'ECSR.
// Chaque module = ensemble de blocs typés (intro, schema_interactif, manipulation, video, cas, quiz).
//
// Contrairement aux 32 leçons REMC pures, ces modules apportent une COUCHE
// visuelle & manipulatoire dédiée aux points techniques les plus difficiles à
// expliquer sans schéma.

// ══════════════════════════════════════════════════════════════════════════
// Liste des modules (pour la landing page)
// ══════════════════════════════════════════════════════════════════════════

export const MODULES_CONDUITE = [
  {
    slug: 'chaine-cinematique',
    code: 'M1',
    title: 'La chaîne cinématique',
    subtitle: 'Comprendre comment la puissance passe du moteur aux roues',
    remc_lecon: 'C1b — Démarrer & doser',
    duree: '25 min',
    difficulty: 'Base',
    color: 'red',
    icon: 'Cog',
    status: 'active',
    tags: ['Mécanique', 'Démarrage', 'Boîte de vitesses'],
    description: "Schéma cliquable, simulateur de point de patinage et jeu du bon rapport. L'élève comprend enfin pourquoi le moteur cale ou broute.",
  },
  {
    slug: 'controles-angles-morts',
    code: 'M2',
    title: 'Contrôles & angles morts',
    subtitle: 'Le geste qui sauve — voir avant d\'agir',
    remc_lecon: 'C1d + C2b',
    duree: '20 min',
    difficulty: 'Base',
    color: 'amber',
    icon: 'Eye',
    status: 'coming_soon',
    tags: ['Sécurité', 'Rétroviseurs', 'Regard'],
    description: "Vue de dessus animée des zones aveugles autour de la voiture, séquences de contrôle avant chaque changement de direction, réflexe du contrôle-épaule.",
  },
  {
    slug: 'indices-formels-informels',
    code: 'M3',
    title: 'Indices formels & informels',
    subtitle: 'Lire la route au-delà des panneaux',
    remc_lecon: 'C2a — Prise d\'informations',
    duree: '30 min',
    difficulty: 'Intermédiaire',
    color: 'sky',
    icon: 'ScanEye',
    status: 'coming_soon',
    tags: ['Anticipation', 'Regard', 'Environnement'],
    description: "Scènes urbaines/rurales où l'élève identifie les signaux formels (panneaux, feux) et informels (regard piéton, roue qui tourne, ombre au sol).",
  },
  {
    slug: 'franchissement-intersections',
    code: 'M4',
    title: 'Franchir une intersection',
    subtitle: 'Ralentir, regarder, décider, franchir',
    remc_lecon: 'C2c — Intersections',
    duree: '25 min',
    difficulty: 'Intermédiaire',
    color: 'emerald',
    icon: 'GitFork',
    status: 'coming_soon',
    tags: ['Priorités', 'Regard', 'Décision'],
    description: "Version pilote qualité pro en cours d'élaboration — vues de dessus finement dessinées, séquence R-R-D-F animée, panneau explicatif contextuel.",
  },
  {
    slug: 'giratoires',
    code: 'M5',
    title: 'Les giratoires',
    subtitle: 'Entrer, tourner, sortir sans stress',
    remc_lecon: 'C2f — Giratoires',
    duree: '25 min',
    difficulty: 'Intermédiaire',
    color: 'fuchsia',
    icon: 'RotateCw',
    status: 'coming_soon',
    tags: ['Priorités', 'Trajectoire', 'Clignotants'],
    description: "Version pilote qualité pro en cours d'élaboration — trajectoires animées par sortie, clignotants synchronisés, angle mort poids lourd visualisé.",
  },
];

export function getModule(slug) {
  return MODULES_CONDUITE.find((m) => m.slug === slug) || null;
}

// ══════════════════════════════════════════════════════════════════════════
// Module 1 · La chaîne cinématique — contenu détaillé
// ══════════════════════════════════════════════════════════════════════════

export const MODULE_CHAINE_CINEMATIQUE = {
  slug: 'chaine-cinematique',
  title: 'La chaîne cinématique',
  intro: "Comprendre où va la puissance quand on appuie sur l'accélérateur, c'est la base de la conduite douce. Ce module te montre ce qui se passe SOUS la carrosserie — moteur, embrayage, boîte, roues — et t'entraîne à choisir le bon rapport, à trouver le point de patinage et à ne plus caler.",
  objectifs_ecsr: [
    "Situer les 6 organes de la chaîne cinématique.",
    "Décrire le rôle et la position de l'embrayage.",
    "Trouver le point de patinage sans caler.",
    "Choisir le rapport adapté à sa vitesse et au relief.",
    "Utiliser le frein moteur en descente.",
  ],

  // ── Bloc 1 · Le schéma cliquable ──
  schema: {
    kicker: "Bloc 1 · Le grand tour",
    titre: "Les 6 organes de la chaîne cinématique",
    consigne: "Clique sur chaque organe pour découvrir son rôle. À la fin, coche mentalement ce que tu as retenu.",
    // Composants ordonnés du moteur aux roues (utilisés pour le SVG interactif)
    organes: [
      {
        id: 'moteur',
        nom: 'Moteur',
        role: 'Produit la puissance',
        detail: "Transforme le carburant (essence, diesel, électricité) en énergie mécanique de rotation. Un moteur essence tourne entre 800 (ralenti) et 6 500 tr/min (rouge). Un moteur diesel entre 800 et 4 500 tr/min.",
        pieges: "Ne pas confondre régime moteur (tr/min) et vitesse du véhicule (km/h). Un moteur qui hurle à l'arrêt = régime élevé sans vitesse.",
      },
      {
        id: 'embrayage',
        nom: 'Embrayage',
        role: 'Le lien vivant',
        detail: "Fait le pont entre le moteur (qui tourne en permanence) et la boîte de vitesses. Quand tu appuies sur la pédale, tu SÉPARES ces deux mondes. Quand tu relâches, tu les REBRANCHES.",
        pieges: "Rouler le pied sur la pédale = user l'embrayage prématurément (patinage permanent). Coût de remplacement : 800-1500 €.",
      },
      {
        id: 'boite',
        nom: 'Boîte de vitesses',
        role: 'Multiplicateur / réducteur',
        detail: "Contient 5 ou 6 paires d'engrenages (les rapports). 1ère = grosse réduction, beaucoup de couple, peu de vitesse. 5ème/6ème = peu de réduction, économique en carburant.",
        pieges: "Passer 2ème à 40 km/h = sur-régime (moteur qui hurle). Passer 5ème à 30 km/h = sous-régime (moteur qui broute).",
      },
      {
        id: 'arbre',
        nom: 'Arbre de transmission',
        role: 'Le tuyau à couple',
        detail: "Barre rigide qui transporte la rotation de la boîte vers les roues motrices. Sur une propulsion, il traverse toute la voiture. Sur une traction (le plus courant), il est court.",
        pieges: "Pièce mécanique invisible mais essentielle — une casse = immobilisation immédiate.",
      },
      {
        id: 'differentiel',
        nom: 'Différentiel',
        role: 'Le partageur intelligent',
        detail: "Répartit la puissance entre roue gauche et roue droite. En virage, la roue extérieure doit tourner plus vite que l'intérieure — le différentiel gère ça automatiquement.",
        pieges: "Sans différentiel, tu ne pourrais pas tourner (les roues seraient bloquées à la même vitesse).",
      },
      {
        id: 'roues',
        nom: 'Roues motrices',
        role: "L'exécutant final",
        detail: "Reçoivent la rotation et poussent (ou tirent) la voiture au sol via l'adhérence du pneu. Sur route mouillée ou verglacée, l'adhérence chute → risque de patinage.",
        pieges: "Une roue qui patine en démarrage sur verglas = tu accélères trop vite. Solution : démarrer en 2ème pour réduire le couple.",
      },
    ],
  },

  // ── Bloc 2 · Le point de patinage ──
  patinage: {
    kicker: "Bloc 2 · Le geste qui compte",
    titre: "Trouver le point de patinage",
    intro: "Le point de patinage, c'est le moment PRÉCIS où l'embrayage commence à faire le lien entre moteur et roues. Trop tôt = pas de mouvement. Trop tard = calage. Le trouver, c'est la clé du démarrage doux.",
    etapes: [
      { label: "Pédale ENFONCÉE (100%)", pos: 100, etat: "debraye", desc: "Moteur et boîte sont SÉPARÉS. Tu peux passer la 1ère sans dommage." },
      { label: "Remontée LENTE", pos: 60, etat: "debraye", desc: "Les disques s'approchent mais ne se touchent pas encore. La voiture ne bouge pas." },
      { label: "🎯 POINT DE PATINAGE", pos: 40, etat: "patinage", desc: "Premier contact. La voiture commence à AVANCER lentement, même sans accélérer. C'est LE moment à sentir." },
      { label: "Accélération progressive", pos: 20, etat: "patinage", desc: "Tu ajoutes un peu d'accélérateur en continuant à relâcher l'embrayage doucement." },
      { label: "Pédale RELÂCHÉE (0%)", pos: 0, etat: "engaged", desc: "Moteur et boîte sont UNIS. La voiture avance normalement, tu peux enlever le pied." },
    ],
    astuce: "Sur voiture d'auto-école, le point de patinage est généralement à mi-course de la pédale. Sur une voiture perso, il peut être plus haut ou plus bas — d'où l'importance de le REDÉCOUVRIR à chaque voiture différente.",
    erreur_typique: "Relâcher la pédale d'un coup = arrivée brutale au point de patinage = calage. Solution : ralentir la remontée dans les 30 derniers centimètres.",
  },

  // ── Bloc 3 · Choisir le bon rapport ──
  rapport: {
    kicker: "Bloc 3 · Le bon rapport au bon moment",
    titre: "Quelle vitesse pour quel rapport ?",
    intro: "Chaque rapport a une plage de vitesse optimale. En sortir, c'est user le moteur, consommer plus, ou perdre en reprise.",
    // Chaque rapport a une plage optimale [min, max] en km/h et un régime moteur cible
    rapports: [
      { rapport: 1, plage: [0, 14],  usage: "Démarrer, manœuvrer, arrêt/redémarrage", regime: "1000-2500 tr/min", couleur: 'red' },
      { rapport: 2, plage: [15, 29], usage: "Ville lente, sorties de virage serrées", regime: "1500-3000 tr/min", couleur: 'amber' },
      { rapport: 3, plage: [30, 49], usage: "Ville fluide, agglomération", regime: "1500-3000 tr/min", couleur: 'emerald' },
      { rapport: 4, plage: [50, 69], usage: "Sortie de ville, route de campagne", regime: "1500-3000 tr/min", couleur: 'sky' },
      { rapport: 5, plage: [70, 130], usage: "Route, voie rapide, autoroute", regime: "1500-3000 tr/min", couleur: 'fuchsia' },
    ],
    signaux: {
      sur_regime: {
        titre: "Sur-régime (moteur hurle)",
        symptomes: ["Bruit moteur aigu et fort", "Régime > 3500 tr/min à vitesse constante", "Vibrations excessives"],
        solution: "Monter un rapport (passer 3ème si tu es en 2ème, etc.)",
      },
      sous_regime: {
        titre: "Sous-régime (moteur broute)",
        symptomes: ["À-coups, saccades", "Bruit sourd et grave", "Régime < 1200 tr/min à l'accélération"],
        solution: "Rétrograder d'un rapport (passer 2ème si tu es en 3ème, etc.)",
      },
    },
  },

  // ── Bloc 4 · Vidéos en appui ──
  videos: [
    { id: '6M37IYPibPA', title: 'Boîte de vitesses — comment ça marche', note: '5 min. Animation 3D d\'une boîte manuelle 5 rapports.' },
    { id: 'iaTc5WP_kRQ', title: 'Le point de patinage expliqué en 2 minutes', note: '2 min. Idéal en pratique après le 1er démarrage.' },
  ],

  // ── Bloc 5 · Études de cas ──
  cas: [
    {
      id: 'feu-vert',
      titre: "Le feu passe au vert",
      contexte: "Tu es arrêté au feu rouge, boîte au point mort, pied gauche sur l'embrayage enfoncé, main droite sur le levier. Le feu passe au vert. Que fais-tu, dans quel ordre ?",
      etapes_correctes: [
        "1. Passer la 1ère vitesse (embrayage toujours enfoncé)",
        "2. Regarder devant + rétroviseurs",
        "3. Relâcher DOUCEMENT l'embrayage jusqu'au point de patinage",
        "4. Ajouter un peu d'accélérateur tout en continuant à relâcher",
        "5. Une fois en mouvement, monter en 2ème vers 20 km/h",
      ],
      pieges: "Confondre les étapes 3 et 4 (accélérer d'abord = moteur qui hurle sans avancer). Bien : embrayage EN PREMIER, accélérateur en accompagnement.",
    },
    {
      id: 'descente',
      titre: "Descente de col à 8%",
      contexte: "Tu descends une route de montagne, pente à 8%, tu es en 5ème à 70 km/h. Tu sens que la voiture ACCÉLÈRE toute seule et tu utilises beaucoup le frein — les freins commencent à sentir chaud.",
      bonne_reponse: "Rétrograder en 3ème (voire 2ème selon la pente).",
      explication: "Passer un rapport inférieur active le FREIN MOTEUR : le moteur retient la voiture au lieu que ce soient tes freins mécaniques. Sur une descente longue, c'est INDISPENSABLE — sans ça, tes freins peuvent surchauffer et perdre en efficacité (phénomène de fading).",
      alternatives_fausses: [
        "Rester en 5ème et freiner plus fort → risque de fading des freins.",
        "Débrayer et rouler en roue libre → INTERDIT, dangereux et illégal.",
        "Couper le contact → jamais, la direction assistée et le frein assisté seraient perdus.",
      ],
    },
  ],

  // ── Bloc 6 · Quiz d'ancrage ──
  quiz: [
    {
      id: 'q1',
      question: "Dans quel ordre la puissance circule-t-elle ?",
      options: [
        "Moteur → Roues → Boîte → Embrayage",
        "Moteur → Embrayage → Boîte → Différentiel → Roues",
        "Embrayage → Moteur → Boîte → Roues",
        "Boîte → Moteur → Embrayage → Roues",
      ],
      correct: 1,
      explanation: "Le moteur produit la puissance, l'embrayage fait ou coupe le lien, la boîte adapte la démultiplication, et le différentiel répartit vers les roues.",
    },
    {
      id: 'q2',
      question: "Ton moteur BROUTE (à-coups, régime bas). Que fais-tu ?",
      options: [
        "J'accélère plus fort",
        "Je monte d'un rapport",
        "Je rétrograde d'un rapport",
        "Je débraye",
      ],
      correct: 2,
      explanation: "Broutage = sous-régime = rapport TROP HAUT pour ta vitesse. Il faut RÉTROGRADER (passer d'un rapport inférieur) pour remonter le régime moteur.",
    },
    {
      id: 'q3',
      question: "Le point de patinage se trouve :",
      options: [
        "Quand la pédale d'embrayage est complètement enfoncée",
        "Quand la pédale d'embrayage est complètement relâchée",
        "Au moment PRÉCIS où l'embrayage commence à transmettre du couple aux roues",
        "Juste avant de caler",
      ],
      correct: 2,
      explanation: "Le point de patinage, c'est le premier contact entre les disques d'embrayage — la voiture commence à bouger même sans accélérer. C'est le geste qui distingue un débutant d'un conducteur à l'aise.",
    },
    {
      id: 'q4',
      question: "Tu descends une longue côte. Quelle est la bonne pratique ?",
      options: [
        "Débrayer pour économiser du carburant",
        "Rester au point mort en roue libre",
        "Passer un rapport inférieur pour utiliser le frein moteur",
        "Couper le contact",
      ],
      correct: 2,
      explanation: "Le frein moteur (rapport inférieur enclenché) économise tes freins mécaniques et évite la surchauffe (fading). Le point mort ou moteur coupé sont dangereux et illégaux.",
    },
    {
      id: 'q5',
      question: "Rouler avec le pied POSÉ sur la pédale d'embrayage (même sans appuyer), c'est :",
      options: [
        "Recommandé pour anticiper",
        "Neutre, sans effet",
        "Néfaste — cela use prématurément l'embrayage",
        "Uniquement possible sur boîte automatique",
      ],
      correct: 2,
      explanation: "Même un léger appui provoque un patinage permanent entre les disques d'embrayage — ils s'usent. Coût de remplacement : 800-1500 €. Pied AU SOL à gauche entre chaque changement de rapport.",
    },
  ],
};

// ══════════════════════════════════════════════════════════════════════════
// Module M4 · Franchir une intersection
// ══════════════════════════════════════════════════════════════════════════

export const MODULE_INTERSECTIONS = {
  slug: 'franchissement-intersections',
  title: 'Franchir une intersection',
  intro: "Une intersection, c'est l'endroit où les trajectoires se croisent et où les priorités se décident. Ce module te fait manipuler 5 configurations types — feux, stop, priorité, cédez, complexe — et t'apprend LA séquence à ancrer : Ralentir → Regarder → Décider → Franchir.",
  objectifs_ecsr: [
    "Identifier les 4 types de gestion de priorité (feu, stop, cédez, priorité à droite).",
    "Appliquer la séquence R-R-D-F systématiquement.",
    "Placer son véhicule sur la bonne voie avant chaque intersection.",
    "Reconnaître les indices annonciateurs (panneaux à distance, marquage au sol).",
  ],

  // ── Séquence pédagogique R-R-D-F ──
  sequence: {
    kicker: "Bloc 1 · La séquence à ancrer",
    titre: "R · R · D · F — la méthode ECSR",
    intro: "Peu importe le type d'intersection, la même séquence s'applique. La mémoriser, c'est éviter de se figer devant l'imprévu.",
    etapes: [
      { lettre: 'R', mot: 'Ralentir', detail: "Dès que tu vois un panneau d'annonce (stop, cédez, feu, priorité à droite), tu enlèves le pied de l'accélérateur. Pas de freinage brutal — anticipation." },
      { lettre: 'R', mot: 'Regarder', detail: "Gauche, droite, dans le sens opposé. Ne te fie pas au feu vert seul : vérifie qu'aucun usager (piéton, cycliste, moto) ne s'engage." },
      { lettre: 'D', mot: 'Décider', detail: "Ai-je la priorité ? Est-ce que je peux passer ? Si oui : quel rapport, quelle vitesse ? Si non : je m'arrête au bon endroit (ligne de stop ou avant le passage piéton)." },
      { lettre: 'F', mot: 'Franchir', detail: "Une fois la décision prise, tu franchis franchement — ni trop vite ni au ralenti. Une hésitation dans le carrefour = accident potentiel." },
    ],
  },

  // ── 5 scènes SVG interactives ──
  scenes: [
    {
      id: 'feux',
      component: 'SceneCarrefourFeux',
      titre: '① Carrefour à feux tricolores',
      hint: "Feu vert ≠ priorité absolue. Contrôle les tourne-à-gauche adverses et les piétons. Trajectoire correcte : voie de gauche, rester dans sa file en tournant.",
    },
    {
      id: 'stop',
      component: 'SceneCarrefourStop',
      titre: '② Carrefour avec STOP',
      hint: "L'arrêt DOIT être MARQUÉ (roues immobiles au moins 1 seconde) sur la ligne blanche. Ensuite seulement tu regardes et tu t'engages.",
    },
    {
      id: 'priorite',
      component: 'ScenePrioriteRoutePrio',
      titre: '③ Route prioritaire + cédez',
      hint: "Losange jaune = tu es prioritaire, mais reste vigilant. Triangle pointe en bas = tu dois céder aux véhicules venant de gauche ET de droite sur la route principale.",
    },
    {
      id: 'yield',
      component: 'SceneCarrefourComplexeYield',
      titre: '④ Cédez-le-passage aux 4 branches',
      hint: "Chaque branche cède à celle de sa gauche. Sans mouvement collectif, tout le monde attend. Le premier qui bouge doit signaler ses intentions clairement.",
    },
    {
      id: 'complexe',
      component: 'SceneCarrefourComplexe',
      titre: '⑤ Carrefour multi-voies',
      hint: "Ici il faut LIRE les voies avant d'arriver. Se placer à droite pour aller à droite, au milieu pour continuer tout droit, à gauche pour tourner à gauche.",
    },
  ],

  // ── Étude de cas ──
  cas: {
    titre: "Le stop du dimanche matin",
    contexte: "Il est 7h30 le dimanche. Tu approches d'un STOP sur une route de campagne. Aucun véhicule visible dans les 200 m alentour, la visibilité est parfaite. Que fais-tu ?",
    options: [
      { text: "Je continue sans m'arrêter — il n'y a personne, c'est évident", correct: false },
      { text: "Je ralentis fortement mais je ne m'arrête pas complètement (rolling stop)", correct: false },
      { text: "Je marque l'arrêt complet sur la ligne, je regarde G-D-G, puis je repars", correct: true },
      { text: "Je m'arrête AU MILIEU de l'intersection pour mieux voir", correct: false },
    ],
    explication: "Le STOP est une obligation LÉGALE d'arrêt complet, indépendante de la visibilité. Rolling stop = 135 € + 4 points en moins. Même à 7h30 un dimanche, un scooter, un cycliste, un joggeur peut apparaître d'angle mort. L'arrêt complet = 1 seconde minimum, roues immobiles.",
  },

  quiz: [
    {
      id: 'q1',
      question: "Un feu tricolore vert signifie :",
      options: [
        "Priorité absolue, je passe sans regarder",
        "Autorisation de passer SI la voie est libre et sûre",
        "Priorité sur les piétons uniquement",
        "Passage à 30 km/h maximum",
      ],
      correct: 1,
      explanation: "Le vert autorise le passage MAIS n'exonère pas de la vigilance. Un piéton qui traverse en fin de cycle piéton, un tourne-à-gauche adverse qui coupe — tu restes responsable.",
    },
    {
      id: 'q2',
      question: "À un carrefour avec STOP, l'arrêt doit être :",
      options: [
        "Facultatif si la visibilité est bonne",
        "Marqué obligatoirement (roues immobiles) sur la ligne blanche",
        "Effectué 10 m avant la ligne",
        "D'au moins 5 secondes",
      ],
      correct: 1,
      explanation: "Le STOP impose un arrêt complet, roues immobiles, sur la ligne. Pas de durée minimale légale — mais au moins 1 seconde pour bien regarder G-D-G. Le non-arrêt = 135 € + 4 points.",
    },
    {
      id: 'q3',
      question: "Panneau LOSANGE JAUNE = ",
      options: [
        "Cédez le passage",
        "Route prioritaire",
        "Fin de route prioritaire",
        "Zone piétonne",
      ],
      correct: 1,
      explanation: "Le losange jaune indique une route prioritaire — les véhicules venant des rues transversales doivent te céder le passage.",
    },
    {
      id: 'q4',
      question: "La séquence à appliquer à toute intersection :",
      options: [
        "Regarder → Ralentir → Décider → Franchir",
        "Ralentir → Regarder → Décider → Franchir",
        "Ralentir → Décider → Regarder → Franchir",
        "Franchir → Regarder → Décider → Ralentir",
      ],
      correct: 1,
      explanation: "R-R-D-F : Ralentir d'abord (pour avoir le temps), Regarder ensuite, Décider en fonction, Franchir sans hésitation. L'ordre est crucial : regarder avant de ralentir = trop tard.",
    },
    {
      id: 'q5',
      question: "Dans un cédez-le-passage à 4 branches (sans priorité définie) :",
      options: [
        "Le plus gros véhicule passe en premier",
        "Chaque véhicule cède à celui venant de sa GAUCHE",
        "Chaque véhicule cède à celui venant de sa DROITE",
        "Le premier arrivé passe en premier",
      ],
      correct: 2,
      explanation: "Priorité à droite : chaque conducteur laisse passer celui venant de sa DROITE. Si personne ne bouge, on établit un contact visuel et un des conducteurs cède par courtoisie — mais légalement, c'est la droite qui prime.",
    },
  ],
};

// ══════════════════════════════════════════════════════════════════════════
// Module M5 · Giratoires
// ══════════════════════════════════════════════════════════════════════════

export const MODULE_GIRATOIRES = {
  slug: 'giratoires',
  title: 'Les giratoires',
  intro: "Le giratoire n'est PAS un carrefour comme un autre — la priorité y est INVERSÉE (elle appartient à ceux qui sont déjà dans l'anneau). Ce module te fait manipuler un giratoire simple + un rond-point avec poids lourd et zone d'angle mort révélable.",
  objectifs_ecsr: [
    "Comprendre que la priorité appartient aux véhicules DÉJÀ engagés dans le giratoire.",
    "Placer son véhicule sur la BONNE voie selon la sortie choisie.",
    "Utiliser les clignotants correctement (entrée à droite si première sortie / gauche sinon, sortie toujours à droite).",
    "Identifier et éviter l'angle mort d'un poids lourd.",
  ],

  // ── Bloc "les 4 règles d'or" ──
  regles: {
    kicker: "Bloc 1 · Les 4 règles d'or",
    titre: "Le giratoire en 4 réflexes",
    items: [
      { icone: '🎯', titre: 'Priorité à ceux qui roulent dans l\'anneau', desc: "Tu cèdes le passage à tout véhicule déjà engagé, quelle que soit la voie qu'il occupe." },
      { icone: '↩️', titre: 'Voie extérieure = 1ère sortie ou traversée courte', desc: "Si tu sors à la 1ère ou 2ème sortie, reste sur la voie extérieure. Pas besoin de clignotant à l'entrée." },
      { icone: '↪️', titre: 'Voie intérieure = sortie tardive', desc: "Si tu sors à la 3ème sortie ou plus, clignotant GAUCHE à l'entrée, place-toi sur la voie intérieure, puis clignotant DROIT juste avant ta sortie." },
      { icone: '👉', titre: 'Toujours clignotant droit avant de sortir', desc: "Peu importe la voie ou la sortie, tu annonces ta sortie avec le clignotant droit dès que tu passes la sortie précédant la tienne." },
    ],
  },

  scenes: [
    {
      id: 'giratoire-simple',
      component: 'SceneGiratoire',
      titre: '① Giratoire à 4 branches — trajectoire type',
      hint: "Bruno arrive du sud, veut sortir vers l'ouest (3ème sortie). Trajectoire : clignotant gauche à l'entrée, voie intérieure, puis clignotant droit après la 2ème sortie.",
    },
    {
      id: 'giratoire-pl',
      component: 'SceneGiratoirePL',
      titre: '② Rond-point + poids lourd : l\'angle mort qui tue',
      hint: "Le camion à gauche ne te voit PAS s'il est à ta droite avancée — c'est SA zone d'angle mort révélée en vert par le bouton correction. En vélo, moto ou petite voiture, JAMAIS te placer là.",
    },
  ],

  cas: {
    titre: "3ème sortie d'un rond-point à 5 branches",
    contexte: "Tu arrives sur un rond-point à 5 sorties, tu veux prendre la 3ème (approximativement en face). Ton placement + tes clignotants ?",
    options: [
      { text: "Voie extérieure, aucun clignotant à l'entrée, clignotant droit après la 2ème sortie", correct: false },
      { text: "Voie intérieure, clignotant gauche à l'entrée, clignotant droit après la 2ème sortie", correct: true },
      { text: "Voie extérieure, clignotant droit à l'entrée, clignotant gauche à la sortie", correct: false },
      { text: "Voie du milieu, aucun clignotant (le rond-point n'en demande pas)", correct: false },
    ],
    explication: "La 3ème sortie = sortie tardive → voie INTÉRIEURE et clignotant GAUCHE à l'entrée pour signaler que tu tournes autour. Après la 2ème sortie, tu passes progressivement à la voie extérieure ET tu mets le clignotant DROIT pour annoncer ta sortie.",
  },

  quiz: [
    {
      id: 'q1',
      question: "Dans un giratoire, la priorité appartient à :",
      options: [
        "Ceux qui arrivent (comme un carrefour classique)",
        "Ceux qui sont déjà dans l'anneau",
        "Le plus gros véhicule",
        "Le premier arrivé sur la ligne d'entrée",
      ],
      correct: 1,
      explanation: "Signalisation triangulaire pointe en bas à l'entrée = cédez le passage aux véhicules DÉJÀ engagés. La règle est inversée par rapport à la priorité à droite classique.",
    },
    {
      id: 'q2',
      question: "Pour la 1ère sortie d'un giratoire, tu te places :",
      options: [
        "Sur la voie intérieure avec clignotant gauche",
        "Sur la voie extérieure sans clignotant à l'entrée, clignotant droit avant la sortie",
        "Sur n'importe quelle voie",
        "Toujours au centre",
      ],
      correct: 1,
      explanation: "Sortie proche → voie EXTÉRIEURE + pas de clignotant à l'entrée (tu ne tournes pas autour), clignotant droit dès l'entrée pour annoncer la sortie proche.",
    },
    {
      id: 'q3',
      question: "Le clignotant droit s'utilise dans un giratoire :",
      options: [
        "À l'entrée uniquement",
        "Juste avant chaque sortie que tu prends",
        "Pendant tout le passage dans l'anneau",
        "Jamais, seul le gauche est utilisé",
      ],
      correct: 1,
      explanation: "Clignotant droit = signal de sortie. Tu l'actives juste après avoir passé la sortie qui PRÉCÈDE la tienne — ni trop tôt (confusion) ni trop tard (surprise).",
    },
    {
      id: 'q4',
      question: "Un poids lourd est arrêté à ta gauche dans un rond-point. Tu roules à sa droite. Le camion redémarre. Que faire ?",
      options: [
        "Continuer à sa droite, il me voit dans son rétroviseur",
        "Ralentir et me placer derrière lui — je suis dans son angle mort principal",
        "Le doubler par la droite rapidement",
        "Klaxonner pour le prévenir",
      ],
      correct: 1,
      explanation: "L'avant-droite d'un PL est son plus GROS angle mort. Le chauffeur peut te faire une queue-de-poisson sans te voir en tournant à droite. Toujours se placer DERRIÈRE ou LARGEMENT devant.",
    },
    {
      id: 'q5',
      question: "Le mini-giratoire (îlot central franchissable) :",
      options: [
        "Se franchit comme un giratoire normal",
        "Peut être franchi PAR-DESSUS l'îlot central par les véhicules longs",
        "Est réservé aux poids lourds",
        "Ne suit pas la règle de priorité aux véhicules dans l'anneau",
      ],
      correct: 1,
      explanation: "Sur un mini-giratoire, l'îlot central est PEINT ou légèrement bombé — les bus et camions peuvent le franchir si nécessaire. Les voitures, elles, contournent normalement.",
    },
  ],
};
